// Call this endpoint to fetch the reactform-json for creating template
//api.audiencerate.dev/v1/workspace/w-NXiH1DnzZzS1pkEQ/connectorflow/cnf-nV0eQX9hwkj6/prepare/step1?audienceId=a-JXX5hb38ZWJnKidR
// And render the form using MultiStepFlowFormv2

import ErrorBoundary from '@/components/ErrorBoundary';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { useCompanyValues } from '@/providers/CompanyProvider';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useState } from 'react';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { showToast } from '@/features/toasts/toastsSlice';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { useAppDispatch } from '@/reducers';
import { ToastType } from '@/models/toast';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { RouteConstants } from '@/features/router/RouteConstants';
import { WHATSAPP_TEMPLATE_CONNECTOR_FLOW_NAME } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/constants';

const AUDIENCE_ID = 'a-JXX5hb38ZWJnKidR';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

export const WhatsappTemplateWorkaround = () => {
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const [createFlowInstance] = useCreateFlowInstanceMutation();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  // Call connectorflow endpoint and parse connector_instance_id and whatsapp_connector_id
  const { data: connectorInstances, isLoading: isConnectorInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const { data: connectorFlows, isLoading: isConnectorFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId, queryParams: { showUserJourney: true } },
    { skip: !workspaceId }
  );

  const whatsappConnectorFlow = connectorFlows?.find((flow) => flow.name === WHATSAPP_TEMPLATE_CONNECTOR_FLOW_NAME);
  const whatsappConnectorFlowId = whatsappConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === whatsappConnectorFlow?.connectorId)?.id;

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  // Fetch the prepare data when we have the required IDs
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);

  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: whatsappConnectorFlowId,
        audienceId: AUDIENCE_ID,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      // const translatedParamsConfig = translateI18nValues(paramsConfig, currentLanguage);

      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      setPrepareResult(prepareResultResponse as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!whatsappConnectorFlowId) {
      fetchPrepareData();
    }
  }, [connectorInstanceId, whatsappConnectorFlowId, prepareStep, currentLanguage]);

  type CreateTemplateOpts = {
    flowParams?: FlowFormData;
    flowMappings?: FlowFormData;
    /** When true, skip mappings-form validation (flow finished without a mappings UI). */
    fromFlowComplete?: boolean;
  };

  const handleCreateTemplate = async (opts?: CreateTemplateOpts) => {
    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    const mappingsUiRequired = !!multiStepMappingsConfig?.schema?.properties && Object.keys(multiStepMappingsConfig.schema.properties).length > 0;
    if (!opts?.fromFlowComplete && mappingsUiRequired && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    const instance = instances?.find((inst) => inst.connectorId === whatsappConnectorFlow?.connectorId);
    if (!instance) {
      const message = String(t('campaign:noConnectorInstanceFound'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }
    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: whatsappConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId: AUDIENCE_ID,
      flowParams,
      flowMappings,
    });
    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(
        showToast({
          title: 'Template created successfully',
          type: ToastType.SUCCESS,
        })
      );
      // TODO: Redirect to the template list page
      history.push(RouteConstants.whatsappTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('campaign:flowInstanceCreationFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON:cancel' as any,
    onClick: () => {},
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON:confirm' as any,
    onClick: () => void handleCreateTemplate(),
    disabled: !isMappingsFormValid,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const multiStepFlowFormProps = {
    id: 'template-workaround-form',
    showTemplatePreview: true,
    audienceId: AUDIENCE_ID,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: whatsappConnectorFlowId!,
    paramsConfig: prepareResult || {},
    onComplete: (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
      setFormData(allParams);
      setMultiStepMappingsConfig(mappingsConfig);
      setMappingData({});
      setIsMappingsFormValid(false);
      const hasMappingsUi = !!mappingsConfig?.schema?.properties && Object.keys(mappingsConfig.schema.properties).length > 0;
      if (!hasMappingsUi) {
        void handleCreateTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
      }
    },
  };

  const isTemplateLoading = isConnectorFlowsLoading || isConnectorInstancesLoading || !prepareResult;

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.createWhatsappTemplate')}</h2>
      </div>
      <ErrorBoundary>
        {prepareResult ? (
          <MultiStepFlowForm {...multiStepFlowFormProps} />
        ) : (
          <p className='text-sm text-gray-600'>
            {isTemplateLoading ? t('campaign:whatsappTemplates.templateLoadingMessage') : t('campaign:whatsappTemplates.failedToLoadTemplate')}
          </p>
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        {(() => {
          // Use mappings from last step if available, otherwise fall back to static config
          if (!multiStepMappingsConfig?.schema?.properties || Object.keys(multiStepMappingsConfig?.schema.properties).length === 0) {
            return null;
          }
          return (
            <div>
              <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.dataMapping')}</h3>
              <FlowMappingsForm
                config={multiStepMappingsConfig}
                formData={mappingData}
                onFormDataChange={handleMappingDataChange}
                onValidationChange={handleMappingsValidationChange}
              />
              <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
            </div>
          );
        })()}
      </ErrorBoundary>
    </div>
  );
};
